import { NavLink, useNavigate } from "react-router-dom";
import { createContext, useContext, useState, useEffect } from "react";
import { LayoutDashboard, LogOut, KeyRound, Globe, Settings, Receipt, Activity, Users, Play, Tv, Banknote, Menu, X, PanelLeft, FileText, Building2 } from "lucide-react";
import { toast } from "sonner";

const SidebarContext = createContext({ collapsed: false, toggle: () => {}, rail: false, hidden: false });
export const useSidebar = () => useContext(SidebarContext);

function useIsMobile() {
  const query = "(max-width: 768px)";
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export function SidebarProvider({ children }) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");
  const isMobile = useIsMobile();
  const toggle = () => setCollapsed((prev) => {
    const next = !prev;
    localStorage.setItem("sidebar_collapsed", next ? "1" : "0");
    return next;
  });
  // Desktop collapsed = icons-only rail; mobile collapsed = fully hidden off-screen.
  const rail = collapsed && !isMobile;
  const hidden = collapsed && isMobile;
  return <SidebarContext.Provider value={{ collapsed, toggle, isMobile, rail, hidden }}>{children}</SidebarContext.Provider>;
}

function SidebarToggle() {
  const { hidden, toggle } = useSidebar();
  if (!hidden) return null; // only the mobile pullout tab; desktop uses the always-visible rail
  return (
    <button
      onClick={toggle}
      data-testid="sidebar-toggle"
      aria-label="Show menu"
      className="fixed top-1/2 -translate-y-1/2 left-0 z-50 h-16 w-6 rounded-r-lg bg-white border border-l-0 border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:bg-slate-50 hover:w-7 transition-all duration-200"
    >
      <Menu size={16} strokeWidth={1.75} />
    </button>
  );
}

function MainArea({ children }) {
  const { rail, hidden } = useSidebar();
  return <main className={`main-content ${rail ? "main-content--rail" : ""} ${hidden ? "main-content--collapsed" : ""}`}>{children}</main>;
}

export function DashboardShell({ sidebar, children }) {
  return (
    <SidebarProvider>
      <div className="app-layout">
        <SidebarToggle />
        {sidebar}
        <MainArea>{children}</MainArea>
      </div>
    </SidebarProvider>
  );
}

function SidebarShell({ children, username, role }) {
  const navigate = useNavigate();
  const { collapsed, toggle, rail, hidden, isMobile } = useSidebar();
  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    toast.success("Signed out");
    navigate("/login");
  };

  const roleLabel = role === "admin" ? "Administrator" : role === "user" ? "Internal User" : role === "product" ? "Product" : "Partner";

  return (
    <div className={`${rail ? "w-[68px]" : "w-[220px]"} h-screen bg-white border-r border-slate-200 flex flex-col fixed left-0 top-0 z-40 transition-all duration-300 ${hidden ? "-translate-x-full" : "translate-x-0"}`}>
      {/* Brand */}
      {rail ? (
        <div className="py-6 border-b border-slate-100 flex justify-center">
          <button
            onClick={toggle}
            data-testid="sidebar-toggle-expand"
            aria-label="Expand menu"
            title="Expand menu"
            className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            <PanelLeft size={18} strokeWidth={1.75} />
          </button>
        </div>
      ) : (
        <div className="px-5 py-6 border-b border-slate-100 relative">
          <button
            onClick={toggle}
            data-testid="sidebar-toggle-close"
            aria-label="Collapse menu"
            title="Collapse menu"
            className="absolute top-3 right-3 w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            {isMobile ? <X size={16} strokeWidth={1.75} /> : <PanelLeft size={16} strokeWidth={1.75} />}
          </button>
          <p className="text-[10px] font-semibold uppercase text-slate-400 tracking-widest">CloudTV's</p>
          <h1 className="text-xl text-slate-900" style={{ fontFamily: "'Shrikhand', cursive" }}>SilverLine</h1>
          <p className="text-[10px] text-slate-400 mt-0.5">{roleLabel} Panel</p>
        </div>
      )}

      {/* Navigation */}
      <nav className={`flex-1 py-4 space-y-1 overflow-y-auto overflow-x-hidden ${rail ? "px-2" : "px-3"}`}>
        {children}
      </nav>

      {/* Footer */}
      <div className={`py-4 border-t border-slate-100 ${rail ? "px-2" : "px-4"}`}>
        {!rail && (
          <div className="mb-3">
            <p className="text-sm font-medium text-slate-800 truncate">{username}</p>
            <p className="text-[11px] text-slate-400">{roleLabel}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={`sidebar-nav-item w-full text-red-500 hover:bg-red-50 ${rail ? "justify-center px-0" : ""}`}
          data-testid="nav-signout"
          title={rail ? "Sign Out" : undefined}
        >
          <LogOut size={18} strokeWidth={1.5} />
          {!rail && <span>Sign Out</span>}
        </button>
      </div>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testId, disabled, badge, end }) {
  const { rail } = useSidebar();
  if (disabled) {
    return (
      <div className={`sidebar-nav-item opacity-40 cursor-not-allowed ${rail ? "justify-center px-0" : ""}`} data-testid={testId} title={rail ? label : undefined}>
        <Icon size={18} strokeWidth={1.5} />
        {!rail && <span>{label}</span>}
        {!rail && badge && <span className="ml-auto text-[9px] font-medium bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">{badge}</span>}
      </div>
    );
  }
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''} ${rail ? "justify-center px-0" : ""}`} data-testid={testId} title={rail ? label : undefined}>
      <Icon size={18} strokeWidth={1.5} />
      {!rail && <span>{label}</span>}
    </NavLink>
  );
}

function SectionLabel({ label }) {
  const { rail } = useSidebar();
  if (rail) return <div className="my-2 mx-2 border-t border-slate-100" />;
  return <p className="text-[10px] font-semibold uppercase text-slate-400 tracking-wider px-3 pt-4 pb-1">{label}</p>;
}

export function AdminSidebar() {
  const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
  return (
    <SidebarShell username={user.username || "Admin"} role="admin">
      <NavItem to="/admin" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" end />
      <NavItem to="/admin/billing" icon={Receipt} label="Billing" testId="nav-billing" />
      <NavItem to="/admin/statements" icon={FileText} label="Statements" testId="nav-statements" />

      <SectionLabel label="Data Sources" />
      <NavItem to="/admin/web-indexing" icon={Globe} label="Web-indexing" testId="nav-web-indexing" />
      <NavItem to="/admin/advertisements" icon={Play} label="FAST TV Ads" testId="nav-advertisements" />
      <NavItem to="/admin/springserve" icon={Tv} label="SpringServe" testId="nav-springserve" />

      <SectionLabel label="Settings" />
      <NavItem to="/admin/ad-costs" icon={Banknote} label="Ad Server Costs" testId="nav-ad-costs" />
      <NavItem to="/admin/users" icon={Users} label="User Management" testId="nav-users" />
      <NavItem to="/admin/activity" icon={Activity} label="Activity Log" testId="nav-activity" />
      <NavItem to="/admin/settings/password" icon={KeyRound} label="Password" testId="nav-password" />
    </SidebarShell>
  );
}

export function InternalUserSidebar() {
  const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
  return (
    <SidebarShell username={user.username || user.name || "User"} role="user">
      <NavItem to="/user" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" end />
      <NavItem to="/user/billing" icon={Receipt} label="Billing" testId="nav-billing" />
      <NavItem to="/user/statements" icon={FileText} label="Statements" testId="nav-statements" />
      <NavItem to="/user/ad-costs" icon={Banknote} label="Ad Server Costs" testId="nav-ad-costs" />
    </SidebarShell>
  );
}

export function PartnerSidebar() {
  const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
  return (
    <SidebarShell username={user.username || user.partner_name || user.cp_name || "Partner"} role="partner">
      <NavItem to="/partner" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" end />
      <NavItem to="/partner/billing" icon={Receipt} label="Billing" testId="nav-billing" />
    </SidebarShell>
  );
}

export function ProductSidebar() {
  const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
  return (
    <SidebarShell username={user.username || user.name || "Product"} role="product">
      <NavItem to="/product" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" end />
      <NavItem to="/product/billing" icon={Receipt} label="Billing" testId="nav-billing" />

      <SectionLabel label="Data Sources" />
      <NavItem to="/product/web-indexing" icon={Globe} label="Web-indexing" testId="nav-web-indexing" />
      <NavItem to="/product/advertisements" icon={Play} label="FAST TV Ads" testId="nav-advertisements" />
      <NavItem to="/product/springserve" icon={Tv} label="SpringServe" testId="nav-springserve" />

      <SectionLabel label="Settings" />
      <NavItem to="/product/companies" icon={Building2} label="Companies" testId="nav-companies" />
      <NavItem to="/product/ad-costs" icon={Banknote} label="Ad Server Costs" testId="nav-ad-costs" />
      <NavItem to="/product/activity" icon={Activity} label="Activity Log" testId="nav-activity" />
    </SidebarShell>
  );
}
